import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { markRaw } from 'vue';

import store from '@/store/store';
import { RootMutations } from '@/store/types/mutation-types';
import { combineSignals } from '@/bundles/Http/utils/combineSignals';
import { REQUEST_CANCELLED_MESSAGE } from '@/bundles/Http/mappers/ErrorMapper';

export interface ICancellableRequestConfig extends AxiosRequestConfig {
  cancelController?: AbortController;
  options?: { persistOnRouteChange?: boolean };
}

/**
 * Drops the cancel token of a settled request. Without it the store's `cancelTokens`
 * array grows for the lifetime of the tab on pages that never change the route.
 */
export function removeCancelToken (config?: ICancellableRequestConfig): void {
  if (config?.cancelController) {
    store.commit(RootMutations.REMOVE_CANCEL_TOKEN, config.cancelController);
  }
}

/**
 * A request cut off by a page unload can settle with status 0 and an empty body (Firefox fires
 * `loadend` without `abort`), and axios resolves any status-less response as a success. Callers
 * would then read `''.data` and show the TypeError as a notification on the way out of the page,
 * so such a response is rejected as cancelled, which the notifications drop.
 */
export function responseSuccessInterceptor<T extends AxiosResponse | undefined> (response: T): T {
  removeCancelToken(response?.config);
  if (response && !response.status) {
    throw Error(REQUEST_CANCELLED_MESSAGE);
  }
  return response;
}

/**
 * Registers an abort controller for a GET so a route change can cancel it.
 * Shared by both axios pipelines (the v2 client and the legacy v1 instance) so
 * registration and removal cannot drift apart.
 */
export function registerCancelToken (config: ICancellableRequestConfig): void {
  if (config.method?.toLowerCase() !== 'get') {
    return;
  }

  // markRaw keeps the controller out of Vue's reactivity system: the store only needs
  // it as an identity to abort and to remove by, and a proxy would break that identity.
  const controller = markRaw(new AbortController());

  store.commit(RootMutations.ADD_CANCEL_TOKEN, {
    controller,
    persistOnRouteChange: config.options?.persistOnRouteChange || false,
  });

  config.cancelController = controller;

  const signal = config.signal
    ? combineSignals([config.signal, controller.signal])
    : controller.signal;
  config.signal = signal;

  // An aborted request never reaches the response interceptors with its config
  // (axios rejects with a bare Cancel), so unregister on abort too.
  if (signal.aborted) {
    removeCancelToken(config);
  } else {
    signal.addEventListener('abort', () => removeCancelToken(config), { once: true });
  }
}

export function requestInterceptor (config: ICancellableRequestConfig): AxiosRequestConfig {
  const token = store.state.idToken || store.state.temporaryAuthToken;
  if (token) {
    config.headers = {
      ...config.headers,
      Authorization: `Bearer ${ token }`,
    };
  }
  if (store.state.visitorId) {
    config.headers = {
      ...config.headers,
      'visitor-id': store.state.visitorId!,
    };
  }
  registerCancelToken(config);
  return config;
}
